// src/components/ContinueJobPopup.js
// Shared by ApprovedInvoices and SendInvoices. Rendered through a portal on
// document.body so no page's .modal / .modal-overlay rules can move it, and it
// sits above the Job Details popup it is opened from. Styles are inline on purpose:
// the two pages have different CSS files and different modal classes, and a
// new global class would be one more thing that can bleed across pages.

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const todayKey = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.55)",
    zIndex: 1999998,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  card: {
    width: "min(470px, 94vw)",
    maxHeight: "90vh",
    overflowY: "auto",
    background: "#ffffff",
    borderRadius: 12,
    padding: "22px 24px",
    boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
    color: "#0f172a",
    fontFamily: "inherit",
    position: "relative",
    zIndex: 1999999,
    boxSizing: "border-box",
    textAlign: "left",
  },
  title: { margin: "0 0 6px", fontSize: 17, fontWeight: 800 },
  sub: { margin: "0 0 14px", fontSize: 13, lineHeight: 1.5, color: "#64748b" },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "7px 0",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 13.5,
  },
  rowLabel: { color: "#64748b", fontWeight: 600 },
  rowValue: { fontWeight: 800, textAlign: "right" },
  field: { display: "flex", flexDirection: "column", gap: 5, marginTop: 16 },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#64748b",
  },
  input: {
    padding: "9px 11px",
    font: "inherit",
    fontSize: 14,
    color: "#0f172a",
    background: "#fff",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    boxSizing: "border-box",
    width: "100%",
  },
  note: {
    margin: "14px 0 0",
    padding: "9px 11px",
    fontSize: 12.5,
    lineHeight: 1.5,
    color: "#166534",
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 8,
  },
  error: {
    margin: "14px 0 0",
    padding: "9px 11px",
    fontSize: 12.5,
    lineHeight: 1.5,
    color: "#92400e",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 8,
  },
  actions: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    marginTop: 20,
  },
  cancel: {
    padding: "10px 18px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
    border: "1px solid #94a3b8",
    background: "#f1f5f9",
    color: "#0f172a",
  },
  save: {
    padding: "10px 18px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
    border: "1px solid #15803d",
    background: "#16a34a",
    color: "#fff",
  },
  disabled: { opacity: 0.55, cursor: "not-allowed" },
};

const ContinueJobPopup = ({
  open,
  sourceJob,
  nextWO,
  loading = false,
  saving = false,
  error = "",
  onConfirm,
  onCancel,
}) => {
  const [date, setDate] = useState(todayKey());

  useEffect(() => {
    if (open) setDate(todayKey());
  }, [open, sourceJob?.id]);

  if (!open) return null;

  const busy = loading || saving;
  const canSave = Boolean(sourceJob?.id) && Boolean(date) && !busy;

  return createPortal(
    <div
      style={styles.overlay}
      onClick={() => {
        if (!saving) onCancel();
      }}
    >
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.title}>Continue Job</h3>
        <p style={styles.sub}>
          This creates a new Active job that stems off this work order. The
          current job is not completed and is not changed in any way.
        </p>

        {loading ? (
          <p style={styles.sub}>Looking up the job behind this work order...</p>
        ) : (
          <>
            <div style={styles.row}>
              <span style={styles.rowLabel}>Site</span>
              <span style={styles.rowValue}>{sourceJob?.siteName || "—"}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.rowLabel}>Current work order</span>
              <span style={styles.rowValue}>{sourceJob?.wo || "—"}</span>
            </div>
            <div style={styles.row}>
              <span style={styles.rowLabel}>New work order</span>
              <span style={{ ...styles.rowValue, color: "#0f766e" }}>
                {nextWO || "—"}
              </span>
            </div>
            <div style={styles.row}>
              <span style={styles.rowLabel}>Scope</span>
              <span style={styles.rowValue}>{sourceJob?.scope || "—"}</span>
            </div>

            <label style={styles.field}>
              <span style={styles.fieldLabel}>Start date for the new job</span>
              <input
                type="date"
                style={styles.input}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>

            <p style={styles.note}>
              {sourceJob?.wo || "This job"} stays exactly as it is. A new job{" "}
              <strong>{nextWO || ""}</strong> is added to Active jobs for{" "}
              {date || "the date above"}.
            </p>
          </>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <div style={styles.actions}>
          <button
            type="button"
            style={{ ...styles.cancel, ...(saving ? styles.disabled : {}) }}
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            style={{ ...styles.save, ...(canSave ? {} : styles.disabled) }}
            onClick={() => onConfirm(date)}
            disabled={!canSave}
          >
            {saving ? "Creating..." : "Create New Job"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContinueJobPopup;
